import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, Button, Pagination, Alert } from 'react-bootstrap';
import ClinicService from '../../../services/clinic.service';
import Loading from '../../common/Loading';
import ClinicListFilters from '../ClinicListFilters';
import { formatPmsType } from '../../../constants/pmsTypes';
import '../clinics.css';

const SORT_FIELDS = [
    { value: '', label: 'Name (A–Z)' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'clinicType', label: 'Type' },
    { value: 'dateStarted', label: 'Date Started' },
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Last Modified' },
];

const TYPE_BADGE = {
    ADMIN: 'clinic-type-badge clinic-type-badge--admin',
    EYECONX: 'clinic-type-badge clinic-type-badge--eyeconx',
    SMS_GATEWAY: 'clinic-type-badge clinic-type-badge--sms',
};

const PMS_BADGE = {
    NONE: 'clinic-type-badge clinic-type-badge--pms-none',
    OD_OFFICE: 'clinic-type-badge clinic-type-badge--pms',
    IFILE: 'clinic-type-badge clinic-type-badge--pms',
    OPTOSYS: 'clinic-type-badge clinic-type-badge--pms-optosys',
    OTHER: 'clinic-type-badge clinic-type-badge--pms',
};

const formatLocation = (city, province) => {
    const parts = [city, province].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
};

function ClinicDashboard({ searchPanelOpen = false, onSearchPanelToggle }) {
    const navigate = useNavigate();
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [clinics, setClinics] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedItem, setCopiedItem] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');

    const queryOptions = {
        searchField: 'name',
        searchTerm: appliedSearch,
        typeFilter,
        statusFilter,
        sortBy: sortBy || 'name',
        sortOrder,
    };

    const fetchList = useCallback(() => {
        setIsLoading(true);
        setError(null);
        ClinicService.getAllClinics(currentPage, itemsPerPage, queryOptions)
            .then((res) => {
                setClinics(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                if ((res.data.content || []).length === 0 && (appliedSearch || typeFilter || statusFilter)) {
                    setError('No clinics match your filters.');
                }
            })
            .catch(() => {
                setError('Failed to load clinics. Please try again.');
                setClinics([]);
            })
            .finally(() => setIsLoading(false));
    }, [
        currentPage,
        itemsPerPage,
        appliedSearch,
        typeFilter,
        statusFilter,
        sortBy,
        sortOrder,
    ]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm === appliedSearch) return;
            setCurrentPage(0);
            setAppliedSearch(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, appliedSearch]);

    const copyToClipboard = (event, text, id, type) => {
        event.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedItem({ id, type });
        setTimeout(() => setCopiedItem(null), 2000);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchTerm === appliedSearch) return;
        setCurrentPage(0);
        setAppliedSearch(searchTerm);
    };

    const handleClearNameSearch = () => {
        setSearchTerm('');
        if (appliedSearch) {
            setCurrentPage(0);
            setAppliedSearch('');
        }
    };

    const handleTypeFilterChange = (value) => {
        setTypeFilter(value);
        setCurrentPage(0);
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setCurrentPage(0);
    };

    const handleSortByChange = (value) => {
        setSortBy(value);
        setCurrentPage(0);
    };

    const handleSortOrderChange = (value) => {
        setSortOrder(value);
        setCurrentPage(0);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setTypeFilter('');
        setStatusFilter('');
        setSortBy('');
        setSortOrder('asc');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const openClinic = (id) => navigate(`/clinics/${id}`);

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = clinics.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const CopyButton = ({ value, clinicId, type, title }) => {
        if (!value) return null;
        const copied = copiedItem?.id === clinicId && copiedItem?.type === type;
        return (
            <button
                type="button"
                className="btn btn-link btn-sm clinic-copy-btn p-0 ms-1"
                onClick={(e) => copyToClipboard(e, value, clinicId, type)}
                title={title}
            >
                <i className={`bi ${copied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
            </button>
        );
    };

    const StatusBadge = ({ status }) =>
        status === 'ON' ? (
            <span className="clinic-status-badge clinic-status-badge--on">
                <span className="clinic-status-dot"></span>
                Active
            </span>
        ) : (
            <span className="clinic-status-badge clinic-status-badge--off">
                <span className="clinic-status-dot"></span>
                Inactive
            </span>
        );

    const TypeBadge = ({ type }) => (
        <span className={TYPE_BADGE[type] || 'clinic-type-badge'}>{type || '—'}</span>
    );

    const PmsBadge = ({ pmsType }) => (
        <span className={PMS_BADGE[pmsType] || 'clinic-type-badge'}>
            {formatPmsType(pmsType)}
        </span>
    );

    return (
        <>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3 flex-wrap">
                <div className="mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-building text-primary" aria-hidden="true"></i>
                    <h5 className="card-title mb-0 clinic-list-card__title">
                        Clinic Directory
                        <span className="clinic-list-card__count">
                            ({totalElements} clinic{totalElements !== 1 ? 's' : ''} total)
                        </span>
                    </h5>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                    <form onSubmit={handleSearchSubmit} className="clinic-list-name-search">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text" aria-hidden="true">
                                <i className="bi bi-search"></i>
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by clinic name…"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Search by clinic name"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleClearNameSearch}
                                    aria-label="Clear clinic name search"
                                    title="Clear search"
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>
                    </form>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm flex-shrink-0"
                        onClick={onSearchPanelToggle}
                        aria-expanded={searchPanelOpen}
                        title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                    >
                        <i className="bi bi-funnel me-1"></i>
                        Filter
                    </button>
                </div>
            </div>

            <Collapse in={searchPanelOpen}>
                <div>
                    <ClinicListFilters
                        typeFilter={typeFilter}
                        onTypeFilterChange={handleTypeFilterChange}
                        statusFilter={statusFilter}
                        onStatusFilterChange={handleStatusFilterChange}
                        sortBy={sortBy}
                        onSortByChange={handleSortByChange}
                        sortFields={SORT_FIELDS}
                        sortOrder={sortOrder}
                        onSortOrderChange={handleSortOrderChange}
                        onClear={handleClearFilters}
                        canClear={Boolean(appliedSearch || typeFilter || statusFilter || searchTerm || sortBy)}
                    />
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : error && clinics.length === 0 ? (
                <Alert variant="warning" className="border-0 shadow-sm">
                    {error}
                </Alert>
            ) : (
                <div className="table-responsive clinic-list-table-wrap">
                    <table className="table clinic-list-table align-middle mb-0">
                        <thead>
                            <tr>
                                <th>Clinic</th>
                                <th>Type</th>
                                <th>PMS</th>
                                <th>Status</th>
                                <th>Location</th>
                                <th>Contact</th>
                                <th>Started</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clinics.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-5">
                                        No clinics found.
                                    </td>
                                </tr>
                            ) : (
                                clinics.map((clinic) => (
                                    <tr
                                        key={clinic.id}
                                        className={`clinic-list-row ${clinic.status === 'OFF' ? 'clinic-list-row--inactive' : ''}`}
                                        onClick={() => openClinic(clinic.id)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openClinic(clinic.id);
                                            }
                                        }}
                                    >
                                        <td>
                                            <div className="clinic-list-name">{clinic.name}</div>
                                            {clinic.number && (
                                                <div className="clinic-list-meta">
                                                    <span className="clinic-list-number">#{clinic.number}</span>
                                                    <CopyButton
                                                        value={clinic.number}
                                                        clinicId={clinic.id}
                                                        type="number"
                                                        title="Copy clinic number"
                                                    />
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <TypeBadge type={clinic.clinicType} />
                                        </td>
                                        <td>
                                            <PmsBadge pmsType={clinic.pmsType} />
                                        </td>
                                        <td>
                                            <StatusBadge status={clinic.status} />
                                        </td>
                                        <td>
                                            <span className="clinic-list-location">
                                                {formatLocation(clinic.city, clinic.province)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="clinic-list-contact">
                                                <span>
                                                    <i className="bi bi-telephone me-1 text-muted"></i>
                                                    {clinic.phone || '—'}
                                                    <CopyButton
                                                        value={clinic.phone}
                                                        clinicId={clinic.id}
                                                        type="phone"
                                                        title="Copy phone"
                                                    />
                                                </span>
                                                <span className="clinic-list-email">
                                                    <i className="bi bi-envelope me-1 text-muted"></i>
                                                    {clinic.email || '—'}
                                                    <CopyButton
                                                        value={clinic.email}
                                                        clinicId={clinic.id}
                                                        type="email"
                                                        title="Copy email"
                                                    />
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="clinic-list-date">{clinic.dateStarted || '—'}</span>
                                        </td>
                                        <td className="text-end">
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openClinic(clinic.id);
                                                }}
                                                title="Open clinic"
                                            >
                                                Open
                                                <i className="bi bi-arrow-right-short ms-1"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Show</span>
                        <select
                            className="form-select form-select-sm clinic-page-size"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small text-muted">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default ClinicDashboard;
